import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { User, UserOrg } from './user';
import { catchError, map } from 'rxjs/operators';
import { EnvironmentService } from './environment/environment.service';
import { throwError } from 'rxjs';

export interface SessionTimeout {
  id: number;
  organizationId: number;
  roleId: number;
  timeout: number;
  status: boolean;
}

export interface SessionStatus {
  isAuthenticated: boolean;
  userId?: number;
  userName?: string;
  organizationId?: number;
  expiresAt?: string;
}
@Injectable({
  providedIn: 'root'
})
export class UserService {

  constructor(private http: HttpClient, private environmentService: EnvironmentService) { }

  async onLogin(userObj: any): Promise<User> {
    // Construct the API URL dynamically based on the activeAppBaseUrl
    const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Account/login`;
    //console.log("apiUrl==", apiUrl);
    
    try {
      const response = await this.http.post<User>(apiUrl, userObj).toPromise();
      return response;
    } catch (error) {
      console.error('Error during login:', error);
      throw error; // Rethrow or handle the error as needed
    }
  }

  // The authoritative "am I logged in" check - the server, not localStorage,
  // decides this. The endpoint always responds 200 (isAuthenticated: false is a
  // normal, expected answer here, not a failure - see AccountController), so the
  // catch below is only for genuine failures to reach the server at all (network
  // down, CORS misconfigured, etc.), which are treated the same as "not
  // authenticated" rather than left unhandled.
  async getSessionStatus(): Promise<SessionStatus> {
    const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Account/session/status`;
    try {
      return await this.http.get<SessionStatus>(apiUrl).toPromise();
    } catch (error) {
      console.error('Error checking session status:', error);
      return { isAuthenticated: false };
    }
  }

  // Same endpoint as getSessionStatus(), for keep-alive: hitting it pushes the
  // session's server-side expiry forward. Returns null (not "logged out") when the
  // server couldn't be reached, so a network blip is never mistaken for a dead session.
  async keepAliveSession(): Promise<SessionStatus | null> {
    const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Account/session/status`;
    try {
      return await this.http.get<SessionStatus>(apiUrl).toPromise();
    } catch (error) {
      console.error('Error sending session keep-alive:', error);
      return null;
    }
  }

  // Tells the server which organization/role this session is now acting under, so
  // it can recompute the session's sliding timeout from that org's admin-configured
  // value (dbo.tblOrganizationSessionTimeout) instead of the system default used
  // right after login.
  async selectOrganizationSession(organizationId: number, roleId: number): Promise<void> {
    const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Account/session/select-organization`;
    try {
      await this.http.post(apiUrl, { organizationId, roleId }).toPromise();
    } catch (error) {
      console.error('Error selecting organization for session:', error);
    }
  }

  async logoutSession(): Promise<void> {
    const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Account/logout`;
    try {
      await this.http.post(apiUrl, {}).toPromise();
    } catch (error) {
      console.error('Error logging out session:', error);
    }
  }

  getSessionTimeoutsByOrgRole(organizationId: number, roleId: number): Observable<SessionTimeout> {
    const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Account/get-organization-session-timeout-by-user`;
  
    const params = new HttpParams()
      .set('OrganizationId', organizationId)
      .set('RoleId', roleId.toString());
  
    return this.http.get<SessionTimeout>(apiUrl, { params }).pipe(
      catchError((error) => {
        console.error('Error fetching session timeouts:', error);
        return throwError(() => new Error('Error fetching session timeouts. Please try again.'));
      })
    );
  }
  

async getUserList(organizationId: string): Promise<User[]> {

  //let organizationId = localStorage.getItem('selectedOrganizationId');
  //let organizationId = '1';
  // If organizationId is null, set it to 0
  //if (!organizationId) {
   // organizationId = '0';  // Setting it as a string '0', since localStorage stores values as strings
  //}


  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Account/getusers`;
  const params = new HttpParams().set('orgid', organizationId);
  try {
    const response = await this.http.post<User[]>(apiUrl,null, {params}).toPromise();
    return response;
  } catch (error) {
    console.error('Error fetching user list:', error);
    throw error; // Rethrow or handle the error as needed
  }
}
async getAssignedUserList(organizationId: string): Promise<User[]> {


  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Account/getusers-assigned`;
  const params = new HttpParams().set('orgid', organizationId);
  try {
    const response = await this.http.post<User[]>(apiUrl,null, {params}).toPromise();
    return response;
  } catch (error) {
    console.error('Error fetching user list:', error);
    throw error; // Rethrow or handle the error as needed
  }
}
async getUnAssignedUserList(organizationId: string): Promise<User[]> {


  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Account/getusers-unassigned`;
  const params = new HttpParams().set('orgid', organizationId);
  try {
    const response = await this.http.post<User[]>(apiUrl,null, {params}).toPromise();
    return response;
  } catch (error) {
    console.error('Error fetching user list:', error);
    throw error; // Rethrow or handle the error as needed
  }
}
// Updated method to use async/await 
async getUserById(userId: number): Promise<User> {
  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Account/getuserbyid`;
  const params = new HttpParams().set('UserId', userId);

  try {
    // Await the HTTP request to get the user data
    const response = await this.http.post<User>(apiUrl, null, { params }).toPromise();
    return response;
  } catch (error) {
    console.error('Error fetching user:', error);
    throw new Error('Error fetching user. Please try again.');
  }
}

async createUser(user: any): Promise<User> {
  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Account/insertuser`;
  try {
    const response = await this.http.post<User>(apiUrl, user).toPromise();
    return response; // Return the created user response from API
  } catch (error) {
    throw error; // Handle or throw error as needed
  }
}


async updateUser(user: any): Promise<User> {
  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Account/updateuser`;
  try {
    const response = await this.http.put<User>(apiUrl, user).toPromise();
    return response; // Return the updated user response from API
  } catch (error) {
    throw error; // Handle or throw error as needed
  }
}



deleteUser(userId: string): Observable<User> {
  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Account/deleteuser`; 

  // Use HttpParams to set the query parameter
  const params = new HttpParams().set('UserId', userId);

  // Change the request from POST to DELETE
  return this.http.delete<User>(apiUrl, { params }).pipe(
    map((response: User) => response), // Return the response
    catchError((error) => {
      console.error('Error deleting user:', error);
      return throwError(() => new Error('Error deleting user. Please try again.'));
    })
  );
}

 // Method to create a user
assignOrganization(userorg: any): Observable<UserOrg> {
  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Account/insertuserorganization`;
  return this.http.post<UserOrg>(apiUrl, userorg).pipe(
    map((response: UserOrg) => {
      return response; // Return the created user response from API
    })
  );
}
deleteUserOrganization(id: string): Observable<UserOrg> {
  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Account/deleteuserorganization`; 

  // Use HttpParams to set the query parameter
  const params = new HttpParams().set('id', id);

  // Change the request from POST to DELETE
  return this.http.delete<UserOrg>(apiUrl, { params }).pipe(
    map((response: UserOrg) => response), // Return the response
    catchError((error) => {
      console.error('Error deleting user:', error);
      return throwError(() => new Error('Error deleting user. Please try again.'));
    })
  );
}



async checkUserNameExists(userName: string): Promise<User | null> {
  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Account/checkusernameexists`;
  const params = new HttpParams()
    .set('userName', userName);

  try {
    const response = await this.http.get<User>(apiUrl, { params }).toPromise(); // Deprecated in RxJS 7+
    return response;
  } catch (error) {
    console.error('Error checking user existence:', error);
    return null;
  }
}


async checkEmailExists( email: string): Promise<User | null> {
  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Account/checkemailexists`;
  const params = new HttpParams()
    .set('email', email); // Add emailId as a query parameter

  try {
    const response = await this.http.get<User>(apiUrl, { params }).toPromise(); // Deprecated in RxJS 7+
    return response;
  } catch (error) {
    console.error('Error checking user email existence:', error);
    return null;
  }
}

}
